import { NavLink, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { BrandMark } from './components/BrandMark';
import { KanbanDashboard } from './routes/KanbanDashboard';
import { RunAudit } from './routes/RunAudit';
import { Settings } from './routes/Settings';
import { StudiosPage } from './features/settings/StudiosPage';
import { OntologyPage } from './features/settings/OntologyPage';
import { CorrectionsPage } from './features/settings/CorrectionsPage';
import { EvalPage } from './features/settings/EvalPage';
import { TestGuidancePage } from './features/settings/TestGuidancePage';
import { CalibrationGuidancePage } from './features/settings/CalibrationGuidancePage';
import { EvidenceLabelsPage } from './features/settings/EvidenceLabelsPage';
import { UsersPage } from './features/settings/UsersPage';
import { GlobalLogTray } from './components/GlobalLogTray';
import { ActiveWorkStrip } from './components/ActiveWorkStrip';
import { DashboardLayout } from './layout/DashboardLayout';
import { PopulateTexecPanel } from './features/phase0/PopulateTexecPanel';
import { PullSourcePanel } from './features/phase1/PullSourcePanel';
import { ImagesPanel } from './features/phase2/ImagesPanel';
import { AIValidationPanel } from './features/phase3/AIValidationPanel';
import { EnvLinksPanel } from './features/phase4/EnvLinksPanel';
import { BugReviewPanel } from './features/phase5/BugReviewPanel';
import { PreconditionsPanel } from './features/phase-preconditions/PreconditionsPanel';
import { VideoAnalyzerPage } from './features/extras-video/VideoAnalyzerPage';
import { GameKnowledgePage } from './features/extras-game-kb/GameKnowledgePage';
import { LookAtThatPage } from './features/extras-look-at-that/LookAtThatPage';
import { StakeEnginePage } from './features/extras-stake-engine/StakeEnginePage';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Login } from './auth/Login';

function LegacyRedirect({ phase }: { phase: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/runs/${id}/phase/${phase}`} replace />;
}

function AuthenticatedApp() {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="login-screen">
        <p className="muted">Checking session…</p>
      </div>
    );
  }
  if (!user) return <Login />;

  return (
    <div className="app-root">
      <ActiveWorkStrip />
      <Routes>
        <Route path="/" element={<HomeShell onLogout={() => void logout()} userEmail={user.email} />}>
          <Route index element={<KanbanDashboard />} />
          <Route path="home" element={<Navigate to="/" replace />} />
          <Route path="board" element={<Navigate to="/" replace />} />
          <Route path="runs/new" element={<Navigate to="/?open=custom" replace />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/studios" element={<StudiosPage />} />
          <Route path="settings/glossary" element={<Navigate to="/settings/ontology" replace />} />
          <Route path="settings/ontology" element={<OntologyPage />} />
          <Route path="settings/corrections" element={<CorrectionsPage />} />
          <Route path="settings/eval" element={<EvalPage />} />
          <Route path="settings/test-guidance" element={<TestGuidancePage />} />
          <Route path="settings/calibration" element={<CalibrationGuidancePage />} />
          <Route path="settings/evidence-labels" element={<EvidenceLabelsPage />} />
          <Route path="settings/users" element={<UsersPage />} />
        </Route>
        <Route path="/runs/:id" element={<DashboardLayout />}>
          <Route index element={<Navigate to="phase/1" replace />} />
          <Route path="phase/0" element={<PreconditionsPanel />} />
          <Route path="phase/1" element={<PopulateTexecPanel />} />
          <Route path="phase/2" element={<EnvLinksPanel />} />
          <Route path="phase/3" element={<PullSourcePanel />} />
          <Route path="phase/4" element={<ImagesPanel />} />
          <Route path="phase/5" element={<VideoAnalyzerPage />} />
          <Route path="phase/6" element={<GameKnowledgePage />} />
          <Route path="phase/7" element={<AIValidationPanel />} />
          <Route path="bugs" element={<BugReviewPanel />} />
          <Route path="look-at-that" element={<LookAtThatPage />} />
          <Route path="videos" element={<LegacyRedirect phase="5" />} />
          <Route path="game-kb" element={<LegacyRedirect phase="6" />} />
          <Route path="stake-engine" element={<StakeEnginePage />} />
          <Route path="audit" element={<RunAudit />} />
          <Route path="setup" element={<LegacyRedirect phase="3" />} />
          <Route path="wizard" element={<LegacyRedirect phase="7" />} />
        </Route>
      </Routes>
      <GlobalLogTray />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

function HomeShell({ onLogout, userEmail }: { onLogout: () => void; userEmail: string }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <BrandMark to="/" />
        <nav className="row" style={{ gap: 16, alignItems: 'center' }}>
          <NavLink to="/" end>
            Board
          </NavLink>
          <NavLink to="/settings">Settings</NavLink>
          <span className="muted small">{userEmail}</span>
          <button type="button" className="btn" onClick={onLogout}>
            Sign out
          </button>
        </nav>
      </header>
      <main className="main">
        <div className="container" style={{ maxWidth: '100%' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
